import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getEmployeeLeads, 
  updateLeadStatus,
  updatePhoneStatus,
  updateLeadContact,
  logCall,
  getCallLogs,
  getChatMessages,
  subscribeToChatMessages,
  markChatMessagesAsRead,
  updateChatStatus,
  updateEmployeeStatus,
  subscribeToEmployeeStatus
} from '../services/firestoreService';
import { initiateXliteCall, endXliteCall } from '../utils/xliteHelper';
import ChatScreen from './ChatScreen';
import ChallengesScreen from '../challenges/ChallengesScreen';
import Footer from './Footer';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { employeeData, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [callAlreadyInitiated, setCallAlreadyInitiated] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [allMessages, setAllMessages] = useState([]);
  const [currentEmployeeStatus, setCurrentEmployeeStatus] = useState('offline');
  const [showChallenges, setShowChallenges] = useState(false);
  const employeeStatusUnsubscribeRef = useRef(null);

  // Fetch leads when employee data is available
  useEffect(() => {
    // Check if employeeData exists and is valid (support both new structure: name and old: employeeName)
    const employeeId = employeeData?._id || employeeData?.email || employeeData?.contactNumber;
    const employeeName = employeeData?.name || employeeData?.employeeName;
    if (employeeData && employeeId && employeeName) {
      console.log('Fetching leads for employee:', employeeData);
      fetchLeads(employeeData);
    } else if (employeeData !== undefined) {
      // employeeData exists but is incomplete (null or missing required fields)
      console.warn('Employee data incomplete:', employeeData);
      setLoading(false);
    } else {
      // If employeeData is undefined, set a timeout to stop loading after 5 seconds
      // This prevents infinite loading if AuthContext never resolves
      const timeoutId = setTimeout(() => {
        console.warn('Employee data loading timeout - stopping loading state');
        setLoading(false);
      }, 5000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [employeeData]);

  // Subscribe to real-time employee status updates
  useEffect(() => {
    const employeeId = employeeData?._id;
    
    // Clean up previous subscription
    if (employeeStatusUnsubscribeRef.current) {
      employeeStatusUnsubscribeRef.current();
      employeeStatusUnsubscribeRef.current = null;
    }
    
    if (employeeId) {
      // Set employee status to online when dashboard loads
      updateEmployeeStatus(employeeId, 'online').catch(error => {
        console.error('Error updating employee status to online:', error);
      });
      
      // Set up real-time listener for employee status
      const unsubscribe = subscribeToEmployeeStatus(employeeId, (employeeData) => {
        if (employeeData) {
          const status = employeeData.status || 'offline';
          setCurrentEmployeeStatus(status);
          console.log('Employee status updated (real-time):', status);
        }
      });
      
      employeeStatusUnsubscribeRef.current = unsubscribe;
      
      // Set initial status
      if (employeeData?.status) {
        setCurrentEmployeeStatus(employeeData.status);
      } else {
        setCurrentEmployeeStatus('online'); // Default to online when dashboard loads
      }
    }

    // Cleanup function - set status to offline when component unmounts
    return () => {
      if (employeeStatusUnsubscribeRef.current) {
        employeeStatusUnsubscribeRef.current();
        employeeStatusUnsubscribeRef.current = null;
      }
      
      // Set status to offline when dashboard closes
      if (employeeId) {
        updateEmployeeStatus(employeeId, 'offline').catch(error => {
          console.error('Error updating employee status to offline:', error);
        });
      }
    };
  }, [employeeData?._id]);

  // Subscribe to chat messages even when chat is closed to track unread messages
  useEffect(() => {
    const extensionNumber = employeeData?.extensionNumber || employeeData?.extension || employeeData?.exten || employeeData?.ext;
    
    if (!extensionNumber) {
      return;
    }

    // Subscribe to real-time chat messages
    const unsubscribe = subscribeToChatMessages(extensionNumber, (chatMessages) => {
      setAllMessages(chatMessages);
      
      // Calculate unread count (messages from admin that are unread)
      const unreadMessages = chatMessages.filter(msg => 
        msg.from === 'admin' && !msg.isRead
      );
      setUnreadCount(unreadMessages.length);
    });

    // Cleanup: unsubscribe when component unmounts or extensionNumber changes
    return () => {
      unsubscribe();
    };
  }, [employeeData]);

  // Mark messages as read when chat window opens
  useEffect(() => {
    const extensionNumber = employeeData?.extensionNumber || employeeData?.extension || employeeData?.exten || employeeData?.ext;
    
    if (showChat && extensionNumber && unreadCount > 0) {
      // Mark all messages as read when chat opens
      markChatMessagesAsRead(extensionNumber).catch(error => {
        console.error('Error marking messages as read:', error);
      });
      // Reset unread count optimistically
      setUnreadCount(0);
    }
  }, [showChat, employeeData, unreadCount]);

  const fetchLeads = async (employee) => {
    try {
      setLoading(true);
      const employeeId = employee?._id || employee?.email || employee?.contactNumber;
      const employeeName = employee?.name || employee?.employeeName;
      if (!employee || !employeeId || !employeeName) {
        console.error('Invalid employee data:', employee);
        setLeads([]);
        setLoading(false);
        return;
      }
      
      const leadsData = await getEmployeeLeads(employeeId, employeeName);
      // Fallback: Ensure we always have an array, even if the API returns null/undefined
      if (!leadsData) {
        console.warn('No leads data returned from database');
        setLeads([]);
        setLoading(false);
        return;
      }
      setLeads(Array.isArray(leadsData) ? leadsData : []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
      // Only show alert if it's a critical error
      if (error.message && !error.message.includes('permission')) {
        alert('Error loading leads. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Error logging out. Please try again.');
    }
  };

  const handleUpdateLead = async (leadId, updateData, phoneNumber = null) => {
    try {
      await updateLeadContact(leadId, updateData, phoneNumber);
      // Refresh leads
      if (employeeData) {
        await fetchLeads(employeeData);
      }
      setShowUpdateModal(false);
      alert('Lead updated successfully!');
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Error updating lead. Please try again.');
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await updateLeadStatus(leadId, newStatus);
      // Refresh leads
      if (employeeData) {
        await fetchLeads(employeeData);
      }
      alert('Lead status updated successfully!');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status. Please try again.');
    }
  };

  const handlePhoneStatusChange = async (leadId, phoneNumber, newStatus) => {
    try {
      // Optimistically update local state first for instant UI feedback
      setLeads(prevLeads => {
        return prevLeads.map(lead => {
          if (lead._id === leadId) {
            const updatedPhoneStatuses = {
              ...(lead.phoneStatuses || {}),
              [phoneNumber]: newStatus
            };
            return {
              ...lead,
              phoneStatuses: updatedPhoneStatuses,
              // Also update main status if this is the only phone or if status logic requires it
              status: lead.status || newStatus
            };
          }
          return lead;
        });
      });

      // Update in Firestore (fire and forget - we already updated UI)
      await updatePhoneStatus(leadId, phoneNumber, newStatus);
      // Don't show alert for individual phone status changes to avoid spam
    } catch (error) {
      console.error('Error updating phone status:', error);
      // Revert optimistic update on error by refetching
      if (employeeData) {
        await fetchLeads(employeeData);
      }
      alert('Error updating phone status. Please try again.');
    }
  };

  const handleCall = (lead) => {
    setSelectedLead(lead);
    setShowCallModal(true);
    // Fetch call logs for this lead
    fetchCallLogs(lead._id);
  };

  const fetchCallLogs = async (leadId) => {
    try {
      if (!leadId) {
        console.warn('No lead ID provided for fetching call logs');
        setCallLogs([]);
        return;
      }
      const logs = await getCallLogs(leadId);
      // Fallback: Ensure we always have an array, even if the API returns null/undefined
      setCallLogs(Array.isArray(logs) ? logs : []);
    } catch (error) {
      console.error('Error fetching call logs:', error);
      // Don't show error to user, just set empty array
      setCallLogs([]);
    }
  };

  const handleCallSubmit = async (callData) => {
    try {
      const employeeId = employeeData?._id || employeeData?.email || employeeData?.contactNumber;
      const employeeName = employeeData?.name || employeeData?.employeeName || 'Unknown';
      await logCall({
        leadId: selectedLead._id,
        employeeId: employeeId,
        employeeName: employeeName,
        phoneNumber: callData.phoneNumber,
        callDuration: callData.callDuration || 0,
        callStatus: callData.callStatus,
        notes: callData.notes,
        callStartTime: callData.callStartTime
      });
      
      // Update phone-specific status if changed
      if (callData.newStatus && callData.phoneNumber) {
        await handlePhoneStatusChange(selectedLead._id, callData.phoneNumber, callData.newStatus);
      } else if (callData.newStatus) {
        // Fallback to lead status update if no phone number specified
        await handleStatusChange(selectedLead._id, callData.newStatus);
      }
      
      setShowCallModal(false);
      setSelectedLead(null);
      alert('Call logged successfully!');
      
      // Refresh leads
      if (employeeData) {
        await fetchLeads(employeeData);
      }
    } catch (error) {
      console.error('Error logging call:', error);
      alert('Error logging call. Please try again.');
    }
  };

  // Extract phone numbers from lead data
  const extractPhoneNumbers = (lead) => {
    if (!lead.leadData || !Array.isArray(lead.leadData)) return [];
    
    return lead.leadData
      .map(item => {
        if (typeof item === 'string' && item.includes('contactNumber:')) {
          return item.replace('contactNumber:', '').trim();
        }
        return item;
      })
      .filter(phone => /^\d{10}$/.test(phone));
  };

  // Filter leads (we'll filter after flattening to phone numbers)
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchTerm === '' || 
      lead.leadName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.assignedTo?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Filter leads by status for sidebar navigation
  const getFilteredLeadsByStatus = (status) => {
    if (status === 'dashboard' || status === 'today') {
      return filteredLeads;
    }
    return filteredLeads.filter(lead => {
      if (status === 'follow_up') {
        return lead.status === 'follow_up';
      } else if (status === 'interested') {
        return lead.status === 'interested';
      } else if (status === 'not_interested') {
        return lead.status === 'not_interested';
      } else if (status === 'callbacks') {
        // Callbacks typically means leads that need follow-up or are in follow_up status
        return lead.status === 'follow_up' || lead.status === 'contacted';
      }
      return true;
    });
  };

  // Filter today's leads
  const getTodayLeads = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return leads.filter(lead => {
      if (!lead.createdAt) return false;
      const leadDate = new Date(lead.createdAt);
      leadDate.setHours(0, 0, 0, 0);
      return leadDate.getTime() === today.getTime();
    });
  };

  const todayLeads = getTodayLeads();

  // Get leads based on current filter
  const getCurrentLeads = () => {
    if (statusFilter === 'today') {
      return todayLeads;
    } else if (statusFilter === 'dashboard') {
      return filteredLeads;
    } else {
      return getFilteredLeadsByStatus(statusFilter);
    }
  };

  const currentLeads = getCurrentLeads();

  // Flatten leads into individual phone number entries
  const flattenLeadsToPhoneNumbers = (leadsArray) => {
    const flattened = [];
    leadsArray.forEach(lead => {
      const phoneNumbers = extractPhoneNumbers(lead);
      if (phoneNumbers.length > 0) {
        phoneNumbers.forEach(phone => {
          // Get phone-specific data from phoneClientData if available
          const phoneClientData = lead.phoneClientData?.[phone];
          const phoneStatus = phoneClientData?.status || lead.phoneStatuses?.[phone] || lead.status || 'new';
          const phoneRemarks = phoneClientData?.remarks || lead.remarks || '-';
          const phoneName = phoneClientData?.clientName || lead.phoneNames?.[phone] || '';
          
          flattened.push({
            leadId: lead._id,
            leadName: phoneName || lead.leadName,
            phoneNumber: phone,
            phoneStatus: phoneStatus,
            remarks: phoneRemarks,
            createdAt: lead.createdAt,
            assignedTo: lead.assignedTo,
            lead: lead // Keep reference to original lead for modals
          });
        });
      } else {
        // If no phone numbers, still show the lead
        flattened.push({
          leadId: lead._id,
          leadName: lead.leadName,
          phoneNumber: null,
          phoneStatus: lead.status || 'new',
          remarks: lead.remarks || '-',
          createdAt: lead.createdAt,
          assignedTo: lead.assignedTo,
          lead: lead
        });
      }
    });
    return flattened;
  };

  // Flatten leads based on current filter
  const currentLeadsFlattened = flattenLeadsToPhoneNumbers(currentLeads);
  
  // For dashboard view, show all leads
  // For other views, show filtered leads
  const displayLeads = statusFilter === 'dashboard' 
    ? flattenLeadsToPhoneNumbers(filteredLeads)
    : currentLeadsFlattened;

  const statusOptions = [
    { value: 'new', label: 'New', color: '#3b82f6' },
    { value: 'contacted', label: 'Contacted', color: '#f59e0b' },
    { value: 'interested', label: 'Interested', color: '#10b981' },
    { value: 'not_interested', label: 'Not Interested', color: '#ef4444' },
    { value: 'follow_up', label: 'Follow Up', color: '#8b5cf6' },
    { value: 'converted', label: 'Converted', color: '#22c55e' }
  ];

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>Trillions CRM</h2>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <div 
              className={`nav-item ${statusFilter === 'dashboard' ? 'active' : ''}`} 
              onClick={() => setStatusFilter('dashboard')}
            >
              <span className="nav-label">Dashboard</span>
            </div>
            <div 
              className={`nav-item ${statusFilter === 'today' ? 'active' : ''}`} 
              onClick={() => setStatusFilter('today')}
            >
              <span className="nav-label">Today's Leads</span>
            </div>
            <div 
              className={`nav-item ${statusFilter === 'follow_up' ? 'active' : ''}`} 
              onClick={() => setStatusFilter('follow_up')}
            >
              <span className="nav-label">Follow up</span>
            </div>
            <div 
              className={`nav-item ${statusFilter === 'interested' ? 'active' : ''}`} 
              onClick={() => setStatusFilter('interested')}
            >
              <span className="nav-label">Interested</span>
            </div>
            <div 
              className={`nav-item ${statusFilter === 'not_interested' ? 'active' : ''}`} 
              onClick={() => setStatusFilter('not_interested')}
            >
              <span className="nav-label">Not interested</span>
            </div>
            <div 
              className={`nav-item ${statusFilter === 'callbacks' ? 'active' : ''}`} 
              onClick={() => setStatusFilter('callbacks')}
            >
              <span className="nav-label">Callbacks</span>
          </div>
            </div>
          <div className="nav-section">
            <div className="nav-item" onClick={handleLogout}>
              <span className="nav-label">Logout</span>
            </div>
          </div>
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {(employeeData?.name || employeeData?.employeeName)?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <div className="user-name">{employeeData?.name || employeeData?.employeeName || 'User'}</div>
              <div className="user-role">{employeeData?.role || 'Employee'}</div>
              <div className={`user-status ${currentEmployeeStatus === 'online' ? 'online' : 'offline'}`}>
                <span className="status-dot"></span>
                {currentEmployeeStatus === 'online' ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-content">
            <button className="menu-toggle-btn" onClick={toggleSidebar}>
              ☰
            </button>
            <div className="header-title-section">
            <h1>Employee Dashboard</h1>
              <span className="header-brand">Trillions Stock Research</span>
            </div>
            <div className="header-actions">
              <button 
                className={`header-nav-btn ${showChallenges ? 'active' : ''}`}
                onClick={() => setShowChallenges(!showChallenges)}
                style={{
                  background: showChallenges ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  fontSize: '14px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  marginRight: '12px'
                }}
                onMouseEnter={(e) => {
                  if (!showChallenges) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showChallenges) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  }
                }}
              >
                Special Leads
              </button>
              <span className="employee-name">Welcome, {employeeData?.name || employeeData?.employeeName || 'User'}</span>
            </div>
          </div>
        </header>

      {/* Special Leads View */}
      {showChallenges && (
        <ChallengesScreen />
      )}

      {/* Dashboard View */}
      {!showChallenges && (
        <>
          {/* Stats - Only show on Dashboard view */}
          {statusFilter === 'dashboard' && (
          <div className="stats-section">
        <div className="stat-card">
          <div className="stat-label">Total Leads</div>
          <div className="stat-value">{leads.length || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Today's Leads</div>
          <div className="stat-value">{todayLeads.length || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">New Leads</div>
          <div className="stat-value">{leads.filter(l => l.status === 'new').length || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Contacted</div>
          <div className="stat-value">{leads.filter(l => l.status === 'contacted').length || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Converted</div>
          <div className="stat-value">{leads.filter(l => l.status === 'converted').length || 0}</div>
        </div>
      </div>
          )}

      {/* Filters */}
      <div className="filters-section">
        <input
          type="text"
          placeholder="Search leads..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

          {/* Leads Table - Dynamic based on filter */}
      <div className="leads-section">
            <h2>
              {statusFilter === 'dashboard' && 'All Leads'}
              {statusFilter === 'today' && `Today's Leads - ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
              {statusFilter === 'follow_up' && 'Follow Up Leads'}
              {statusFilter === 'interested' && 'Interested Leads'}
              {statusFilter === 'not_interested' && 'Not Interested Leads'}
              {statusFilter === 'callbacks' && 'Callbacks'}
              {' '}({displayLeads.length})
            </h2>
            {displayLeads.length === 0 ? (
          <div className="empty-state" style={{ 
            padding: '30px', 
            textAlign: 'center',
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            border: '2px dashed #2a2a2a'
          }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>
                  {statusFilter === 'today' ? '📅' : '🔍'}
                </div>
            <p style={{ color: '#a0a0a0', margin: '0' }}>
                  {statusFilter === 'today' 
                    ? 'No leads assigned today. Check back later or contact your administrator.'
                    : statusFilter === 'dashboard'
                    ? 'No leads found in the database. Leads will appear here once they are assigned to you.'
                    : `No ${statusFilter.replace('_', ' ')} leads found.`}
            </p>
          </div>
        ) : (
          <table className="leads-table">
            <thead>
              <tr>
                    {statusFilter === 'today' ? (
                      <>
                <th>Phone Number</th>
                <th>Status</th>
                <th>Remarks</th>
                <th>Actions</th>
                      </>
                    ) : (
                      <>
                <th>Lead Name</th>
                <th>Phone Number</th>
                <th>Status</th>
                <th>Assigned Date</th>
                <th>Remarks</th>
                <th>Actions</th>
                      </>
                    )}
              </tr>
            </thead>
            <tbody>
                  {displayLeads.map((entry, idx) => {
                const phoneStatusOption = statusOptions.find(s => s.value === entry.phoneStatus);
                
                return (
                  <tr key={`${entry.leadId}-${entry.phoneNumber}-${idx}`}>
                        {statusFilter !== 'today' && (
                    <td>{entry.leadName || 'Unnamed Lead'}</td>
                        )}
                    <td>
                      <span className="phone-badge">{entry.phoneNumber || 'N/A'}</span>
                    </td>
                    <td>
                      <select
                        value={entry.phoneStatus}
                        onChange={(e) => handlePhoneStatusChange(entry.leadId, entry.phoneNumber, e.target.value)}
                        className="phone-status-select"
                        style={{ 
                          backgroundColor: phoneStatusOption?.color || '#3b82f6',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          minWidth: '120px'
                        }}
                        disabled={!entry.phoneNumber}
                      >
                        {statusOptions.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </td>
                        {statusFilter !== 'today' && (
                    <td>{entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : 'N/A'}</td>
                        )}
                    <td className="remarks-cell">{entry.remarks}</td>
                    <td>
                      <div className="phone-actions">
                        {entry.phoneNumber && (
                          <>
                            <button
                              onClick={async () => {
                                    const extension = employeeData?.extensionNumber || '7001';
                                await initiateXliteCall(entry.phoneNumber, extension);
                                setCallAlreadyInitiated(true);
                                const leadWithPhone = { ...entry.lead, selectedPhone: entry.phoneNumber };
                                handleCall(leadWithPhone);
                              }}
                              className="btn-call-small"
                              title={`Call ${entry.phoneNumber} with X-Lite`}
                            >
                              📞 Call
                            </button>
                            <button
                              onClick={() => {
                                const leadWithPhone = { ...entry.lead, selectedPhone: entry.phoneNumber };
                                setSelectedLead(leadWithPhone);
                                setShowUpdateModal(true);
                              }}
                              className="btn-update-small"
                              title={`Update lead for ${entry.phoneNumber}`}
                            >
                              ✏️ Update
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Update Lead Modal */}
      {showUpdateModal && selectedLead && (
        <UpdateLeadModal
          lead={selectedLead}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedLead(null);
          }}
          onUpdate={handleUpdateLead}
        />
      )}

      {/* Call Modal */}
      {showCallModal && selectedLead && (
        <CallModal
          lead={selectedLead}
          callLogs={callLogs}
          callAlreadyInitiated={callAlreadyInitiated}
          onClose={() => {
            setShowCallModal(false);
            setSelectedLead(null);
            setCallLogs([]);
            setCallAlreadyInitiated(false);
          }}
          onSubmit={handleCallSubmit}
        />
      )}

      {/* Sidebar Overlay for mobile */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

        </>
      )}

      {/* Floating Chat Button - Always Visible */}
      <div className="floating-chat-container">
        {showChat && (
          <ChatScreen 
            onClose={() => {
              setShowChat(false);
            }} 
            employeeData={employeeData}
            allMessages={allMessages}
          />
        )}
        <button 
          className="floating-chat-button"
          onClick={async () => {
            const extensionNumber = employeeData?.extensionNumber || employeeData?.extension || employeeData?.exten || employeeData?.ext;
            if (!showChat && extensionNumber) {
              // Update status to online when chat opens
              try {
                await updateChatStatus(extensionNumber, 'online');
              } catch (error) {
                console.error('Error updating chat status:', error);
              }
            }
            setShowChat(!showChat);
          }}
          title={unreadCount > 0 ? `You have ${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : "Open Chat"}
        >
          {showChat ? '✕' : '💬'}
          {!showChat && unreadCount > 0 && (
            <span className="chat-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// Update Lead Modal Component
const UpdateLeadModal = ({ lead, onClose, onUpdate }) => {
  // Get remarks from phoneClientData if available, otherwise from lead
  const phoneNumber = lead.selectedPhone;
  const phoneClientData = lead.phoneClientData?.[phoneNumber];
  const initialRemarks = phoneClientData?.remarks || lead.remarks || '';
  
  const [formData, setFormData] = useState({
    remarks: initialRemarks
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(lead._id, formData, lead.selectedPhone);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            Update Lead
            {lead.selectedPhone && (
              <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#a0a0a0', marginLeft: '10px' }}>
                - {lead.selectedPhone}
              </span>
            )}
          </h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              rows="4"
              placeholder="Add remarks..."
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit">Update</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Call Modal Component
const CallModal = ({ lead, callLogs, callAlreadyInitiated = false, onClose, onSubmit }) => {
  const { employeeData } = useAuth(); // Get employeeData for extension
  
  // Get the selected phone number (the one that was clicked)
  const selectedPhone = lead.selectedPhone || '';
  
  // Filter call logs to show only logs for the selected phone number
  const filteredCallLogs = callLogs.filter(log => 
    log.phoneNumber === selectedPhone
  );

  // Initialize call start time if call was already initiated when modal opened
  const initialStartTime = callAlreadyInitiated ? new Date() : null;
  const [callStartTime, setCallStartTime] = useState(initialStartTime);
  const [currentDuration, setCurrentDuration] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [callData, setCallData] = useState({
    phoneNumber: selectedPhone,
    callDuration: 0,
    callStatus: 'completed',
    newStatus: '',
    notes: '',
    callStartTime: initialStartTime
  });

  // Update duration in real-time when call is active
  useEffect(() => {
    let interval = null;
    if (callStartTime) {
      interval = setInterval(() => {
        const durationMs = new Date() - callStartTime;
        const totalSeconds = Math.floor(durationMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        setCurrentDuration({ hours, minutes, seconds });
      }, 1000); // Update every second
    } else {
      setCurrentDuration({ hours: 0, minutes: 0, seconds: 0 });
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callStartTime]);

  // Format timer display as HH:MM:SS
  const formatTimer = (duration) => {
    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(duration.hours)}:${pad(duration.minutes)}:${pad(duration.seconds)}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!callData.phoneNumber) {
      alert('Please select a phone number');
      return;
    }
    
    // Calculate call duration in minutes if call was started
    let finalDuration = 0;
    if (callStartTime) {
      const endTime = new Date();
      const durationMs = endTime - callStartTime;
      finalDuration = Math.round(durationMs / 60000); // Convert to minutes for storage
    }
    
    // Prepare call data with calculated duration and start time
    const finalCallData = {
      ...callData,
      callDuration: finalDuration,
      callStartTime: callStartTime || new Date()
    };
    
    onSubmit(finalCallData);
  };

  const initiateCall = async (phone) => {
    // Use X-Lite helper to initiate call via HTTP API
    // Format: http://192.168.1.252/call.php?exten=7001&number=7582885143
    // Get employee extension from employeeData (new structure: extensionNumber)
    const extension = employeeData?.extensionNumber || '7001'; // Default fallback
    
    // Record call start time
    const startTime = new Date();
    setCallStartTime(startTime);
    setCallData(prev => ({ ...prev, callStartTime: startTime }));
    
    await initiateXliteCall(phone, extension);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content call-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Make Call - {lead?.leadName || 'Unknown Lead'}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Phone Number</label>
            <div className="phone-selector">
              {selectedPhone ? (
                <div className="phone-option selected" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px',
                  backgroundColor: '#1e3a8a',
                  border: '2px solid #3b82f6',
                  borderRadius: '8px',
                  cursor: 'default'
                }}>
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>
                    📞 {selectedPhone}
                  </span>
                  <span className="call-hint" style={{ fontSize: '12px', color: '#a0a0a0' }}>
                    (SIP: {selectedPhone}@192.168.1.252)
                  </span>
                  {!callAlreadyInitiated && (
                    <button
                      type="button"
                      onClick={async () => {
                        await initiateCall(selectedPhone);
                      }}
                      style={{
                        marginLeft: '10px',
                        padding: '6px 12px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title={`Click to call ${selectedPhone} with X-Lite via SIP`}
                    >
                      Call Now
                    </button>
                  )}
                  {callAlreadyInitiated && (
                    <span style={{
                      marginLeft: '10px',
                      padding: '6px 12px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      ✓ Call Initiated
                    </span>
                  )}
                </div>
              ) : (
                <div className="no-phone-selected">
                  <p>No phone number selected</p>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Call Status</label>
            <div style={{ 
              padding: '16px', 
              backgroundColor: callStartTime ? '#0a0a0a' : '#1a1a1a', 
              border: callStartTime ? '2px solid #0ea5e9' : '2px solid #f59e0b', 
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              {callStartTime ? (
                <>
                  <div style={{ 
                    fontSize: '32px', 
                    fontFamily: 'monospace', 
                    color: '#0ea5e9',
                    marginBottom: '12px',
                    letterSpacing: '2px'
                  }}>
                    {formatTimer(currentDuration)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#a0a0a0', marginBottom: '12px' }}>
                    Call Started: {new Date(callStartTime).toLocaleTimeString()}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      // Get employee extension from employeeData (new structure: extensionNumber)
                      const extension = employeeData?.extensionNumber || '7001'; // Default fallback
                      
                      // End the call session in X-Lite by deleting the API call session
                      await endXliteCall(extension, selectedPhone);
                      
                      // Stop the call timer
                      setCallStartTime(null);
                      // Calculate final duration
                      const endTime = new Date();
                      const durationMs = endTime - callStartTime;
                      const finalDuration = Math.round(durationMs / 60000);
                      // Update call data with final duration
                      setCallData(prev => ({
                        ...prev,
                        callDuration: finalDuration,
                        callStatus: 'completed'
                      }));
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      margin: '0 auto'
                    }}
                    title="End the call"
                  >
                    <span>📞</span>
                    <span>End Call</span>
                  </button>
                </>
              ) : (
                <div style={{ 
                  fontSize: '20px', 
                  color: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <span>📞</span>
                  <span>Ringing...</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Call Status</label>
            <select
              value={callData.callStatus}
              onChange={(e) => setCallData({ ...callData, callStatus: e.target.value })}
            >
              <option value="completed">Completed</option>
              <option value="missed">Missed</option>
              <option value="no_answer">No Answer</option>
            </select>
          </div>

          <div className="form-group">
            <label>Update Lead Status (Optional)</label>
            <select
              value={callData.newStatus}
              onChange={(e) => setCallData({ ...callData, newStatus: e.target.value })}
            >
              <option value="">Keep Current Status</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="not_interested">Not Interested</option>
              <option value="follow_up">Follow Up</option>
              <option value="converted">Converted</option>
            </select>
          </div>

          <div className="form-group">
            <label>Call Notes</label>
            <textarea
              value={callData.notes}
              onChange={(e) => setCallData({ ...callData, notes: e.target.value })}
              rows="4"
              placeholder="Add call notes..."
            />
          </div>

          <div className="call-history">
            <h4>Call History for {selectedPhone || 'N/A'}</h4>
            {filteredCallLogs.length > 0 ? (
              <div className="call-logs">
                {filteredCallLogs.map(log => (
                  <div key={log._id} className="call-log-item">
                    <div className="call-log-header">
                      <span>{log.phoneNumber}</span>
                      <span>{new Date(log.callDate).toLocaleString()}</span>
                    </div>
                    <div className="call-log-details">
                      <span>Status: {log.callStatus}</span>
                      {log.callDuration > 0 && <span>Duration: {log.callDuration} min</span>}
                    </div>
                    {log.notes && <div className="call-log-notes">{log.notes}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ 
                padding: '20px', 
                textAlign: 'center',
                backgroundColor: '#0a0a0a',
                borderRadius: '8px',
                border: '1px dashed #2a2a2a',
                marginTop: '12px'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📞</div>
                <p style={{ color: '#a0a0a0', margin: '0', fontSize: '14px' }}>
                  No call history found for this phone number.
                </p>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit">Log Call</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;


